import {action, computed, makeAutoObservable, runInAction} from 'mobx';
import {AjaxContentType, ApiService} from '../Services';
import {ApiURL, DEVICE_COMPONENTS, SORTING_KEY} from '../AppConstants';
import {
    CommentTDO,
    ComponentTDO,
    MaintenanceDTO,
    MaintenanceStatus,
    MeasurementDTO,
    NewMaintenanceDTO,
    ServiceChangeKey,
    SupportTicketDTO,
    TaskType,
    UserDTO,
} from '../Models/ApiModels';
import {FiltersStore} from './FiltersStore';
import {createContext} from 'react';
import {PromiseCompletion} from '../Services/PromiseCompletion';
import DateTimeService from '../Services/DateTimeService';
import {ConfirmationStore} from "../Components/ConfirmationDialog/ConfirmationDialog";
import {ValueChangedInfo} from "devextreme/ui/editor/editor";
import {LocalStorageGet, LocalStorageRemove, LocalStorageSet} from "../Services/Helpers";
import {FilterSortingState} from "../Components/Tabs/components/ListFilterSorting";
import {authService} from "../index";
import {TokenClaims} from "@azure/msal-common";

type TicketsParams = {
    organizationId: string,
    siteId: string,
    deviceId: string,
    createdOnFrom?: Date | string
    createdOnTo?: Date | string
    modifiedOnFrom?: Date | string
    modifiedOnTo?: Date | string
    closedOnFrom?: Date | string
    closedOnTo?: Date | string
}
export const DefaultServiceRange = [DateTimeService.addDays(DateTimeService.todayLocal(), -5), DateTimeService.endOfDay(DateTimeService.todayLocal())];

export class AppStore {
    public resetGridVisible: boolean = false;
    public filtersStore = new FiltersStore();
    public confirmationStore = new ConfirmationStore();
    public globalLoader: PromiseCompletion = new PromiseCompletion();
    public ticketsUsers: UserDTO[] = [];
    public tickets: SupportTicketDTO[] | null = null;
    public logs: MaintenanceDTO[] | null = null;
    public components: Map<string, ComponentTDO[]> = new Map();
    public measurements: MeasurementDTO[] | null = null;
    public serviceUpdatedFilter: ServiceChangeKey = ServiceChangeKey.isNotResolved;
    public serviceRangeFilter: Array<Date | string> = DefaultServiceRange;
    public selectedServiceRange: Array<Date | string> = DefaultServiceRange;
    public sortingState: FilterSortingState | null = null;
    public serviceLoader: PromiseCompletion = new PromiseCompletion();
    public checklist: MaintenanceDTO[] | null = null;
    public attachmentsData: Map<string, string> = new Map();
    public currentUser = authService.currentUser;
    public isCompanyAdmin = authService.isCompanyAdmin;

    constructor() {
        makeAutoObservable(this);
        void this._init();
    }

    private async _init() {
        this.sortingState = LocalStorageGet<FilterSortingState | null>(SORTING_KEY);
        await this.filtersStore.getData();
    }

    public async loadTicketUsers(deviceId: string) {
        if (!this.filtersStore.selected.customer) {
            this.ticketsUsers = [];
            return;
        }
        const ticketsUsersData = ApiService.getData<UserDTO[]>(`${ApiURL.Devices}/${deviceId}${ApiURL.Permissions}`, null);

        this.serviceLoader.subscribe(ticketsUsersData);
        const {data: ticketsUsers} = await ticketsUsersData;
        runInAction(() => {
            this.ticketsUsers = ticketsUsers;
            const claims = this.currentUser?.idTokenClaims as TokenClaims | undefined;

            this.ticketsUsers = this.isCompanyAdmin ?
                [...ticketsUsers, {id: claims?.sub, userId: claims?.oid, displayName: this.currentUser?.name ?? claims?.name} as UserDTO]
                : ticketsUsers;
        });
    }

    public loadTickets = async () => {
        this.tickets = null;
        if (!this.filtersStore.selected.customer) {
            return;
        }
        try {
            const params: TicketsParams = {
                organizationId: this.filtersStore.selected.customer,
                siteId: this.filtersStore.selected.site,
                deviceId: this.filtersStore.selected.plant,
            };
            const {data: ticketsResp} = await ApiService.getData<SupportTicketDTO[]>(ApiURL.Tickets, params, {completion: this.serviceLoader});
            runInAction(() => {
                this.tickets = ticketsResp?.filter?.(it => this.serviceUpdatedFilter === ServiceChangeKey.isNotResolved ?  !it.isResolved : Boolean(it.isResolved));
            });
        } catch (e) {
            console.log(e);
        }
    };

    public loadTasks = async () => {
        this.checklist = null;
        if (!this.filtersStore.selected.customer || !this.filtersStore.selected.plant) {
            return;
        }
        try {
            const params = {
                organizationId: this.filtersStore.selected.customer,
                siteId: [this.filtersStore.selected.site],
                deviceId: [this.filtersStore.selected.plant]
            };
            const {data: checklist} = await ApiService.getData<MaintenanceDTO[]>(ApiURL.MaintenanceTasks, params, {completion: this.globalLoader});
            runInAction(() => {
                this.checklist = checklist;
            });
        } catch (e) {
            console.log(e);
        }
    };

    private _filterTaskList(list: MaintenanceDTO[] | null) {
        if (this.sortingState && list && list.length) {
            let result = list.slice();
            if (this.sortingState.search) {
                const search = this.sortingState.search.toLocaleLowerCase();
                result = result.filter(e => {
                    return (
                        e.description.includes(search) ||
                        e.status.includes(search) ||
                        e.timeDelta?.includes(search) ||
                        DateTimeService.formatLocal(e.time).includes(search)
                    );
                });
            }
            if (this.sortingState.filters) {
                result = result.filter(e => {
                    let valid = true;
                    const filters = this.sortingState?.filters || {};
                    Object.keys(filters).forEach(key => {
                        if (valid) {
                            if (key === 'type') {
                                const isScheduled = filters[key] === 'isScheduled' && !!e[filters[key] as keyof MaintenanceDTO];
                                const isDataBased = filters[key] !== 'isScheduled' && e.isScheduled === false;
                                valid = isScheduled || isDataBased;
                            } else {
                                valid = e[key as keyof MaintenanceDTO] === filters[key];
                            }
                        }
                    });
                    return valid;
                });
            }
            if (this.sortingState.sorting) {
                result = result.sort((a, b) => {
                        const key = this.sortingState?.sorting as keyof MaintenanceDTO;
                        const v1 = a[key];
                        const v2 = b[key];
                        const isString = typeof v1 === 'string' && typeof v2 === 'string';
                        const isNumber = typeof v1 === 'number' && typeof v2 === 'number';
                        const isDate = typeof v1 === 'object' && typeof v2 === 'object' && DateTimeService.isValidDate(v1 as Date) && DateTimeService.isValidDate(v2 as Date);
                        if (isString) {
                            return (v1 as string)?.localeCompare(v2 as string);
                        } else if (isNumber || isDate) {
                            return (v1 as number) - (v2 as number);
                        }
                        return -1;
                    }
                );
            }
            if (this.sortingState.desc === false) {
                result = result?.reverse();
            }
            return result;
        }
        return list;
    }

    private _filterTicketList(list: SupportTicketDTO[] | null) {
        if (this.sortingState && list && list.length) {
            let result = list.slice();
            if (this.sortingState.search) {
                const search = this.sortingState.search.toLocaleLowerCase();
                result = result.filter(e => {
                    return (
                        e.description.includes(search) ||
                        e?.status?.includes(search) ||
                        e.category?.includes(search) ||
                        e.deviceId.includes(search) ||
                        e.number.includes(search) ||
                        e.priority.includes(search) ||
                        e.title.includes(search) ||
                        e.type.includes(search) ||
                        DateTimeService.formatLocal(e.createdOn).includes(search)
                    );
                });
            }
            if (this.sortingState.filters) {
                result = result.filter(e => {
                    let valid = true;
                    const filters = this.sortingState?.filters || {};
                    Object.keys(filters).forEach(key => {
                        if (valid) {
                            valid = e[key as keyof SupportTicketDTO] === filters[key];
                        }
                    });
                    return valid;
                });
            }
            if (this.sortingState.sorting) {
                result = result.sort((a, b) => {
                        const key = this.sortingState?.sorting as keyof SupportTicketDTO;
                        const v1 = a[key];
                        const v2 = b[key];
                        const isString = typeof v1 === 'string' && typeof v2 === 'string';
                        const isNumber = typeof v1 === 'number' && typeof v2 === 'number';
                        const isDate = typeof v1 === 'object' && typeof v2 === 'object' && DateTimeService.isValidDate(v1 as Date) && DateTimeService.isValidDate(v2 as Date);
                        if (isString) {
                            return (v1 as string)?.localeCompare(v2 as string);
                        } else if (isNumber || isDate) {
                            return (v1 as unknown as number) - (v2 as unknown as number);
                        }
                        return -1;
                    }
                );
            }
            if (this.sortingState.desc === false) {
                result = result?.reverse();
            }
            return result;
        }
        return list;
    }

    @computed
    public get ticketsList() {
        return this._filterTicketList(this.tickets);
    }

    @computed
    public get tasksList() {
        return this._filterTaskList(this.checklist);
    }

    @computed
    public get logsList() {
        return this._filterTaskList(this.logs);
    }

    public setSortingState = (state: FilterSortingState | null) => {
        this.sortingState = state;
        if (state === null) {
            LocalStorageRemove(SORTING_KEY);
        } else {
            LocalStorageSet(SORTING_KEY, state);
        }
    };

    public loadLogs = async () => {
        this.logs = null;
        if (!this.filtersStore.selected.customer || !this.filtersStore.selected.plant) {
            return;
        }
        try {
            const params = {
                organizationId: this.filtersStore.selected.customer,
                siteId: [this.filtersStore.selected.site],
                deviceId: [this.filtersStore.selected.plant],
                status: MaintenanceStatus.Completed
            };
            const {data: logs} = await ApiService.getData<MaintenanceDTO[]>(ApiURL.MaintenanceTasks, params, {completion: this.globalLoader});
            runInAction(() => {
                this.logs = logs;
            });
        } catch (e) {
            console.log(e);
        }
    };

    public loadMeasurements = async () => {
        this.measurements = null;
        if (!this.filtersStore.selected.plant) {
            return;
        }
        try {
            const {data: checklistResp} = await ApiService.getData<MeasurementDTO[]>(`${ApiURL.Devices}/${this.filtersStore.selected.plant}/measurements`);
            this.measurements = checklistResp;
        } catch (e) {
            console.log(e);
        }
    };

    public async loadChatData(ticketId: string) {
        const {data} = await ApiService.getData<CommentTDO[]>(`${ApiURL.Tickets}/${ticketId}/comments`, {}, {completion: this.globalLoader});
        const commentList = data.map(e => {
            // const userName = this.ticketsUsers.find(u => u.id === e.userId)?.displayName;
            return {...e, createdOn: new Date(e.createdOn)};
        }).sort((a, b) => a.createdOn.getTime() - b.createdOn.getTime());
        commentList.filter(e => !!e.attachments.length).forEach(e => {
            e.attachments.forEach(a => {
                if (!this.attachmentsData.get(a.id)) {
                    void new Promise(async () => {
                        const {data: fileData} = await ApiService.getData(`${ApiURL.Tickets}/${ticketId}/comments/${e.id}/attachments/${a.id}`, {},
                            {
                                responseType: 'blob'
                            });
                        runInAction(()=>{
                            this.attachmentsData.set(a.id, URL.createObjectURL(new Blob([fileData as BlobPart], {type: 'application/octet-stream'})));
                        })
                    })
                }

            });
        });
        return commentList;
    }

    public get attachmentKeys() {
        return Array.from(this.attachmentsData.keys());
    }

    public async saveMessage(id: string, message?: string) {

        if (message !== undefined) {
            return ApiService.postData<CommentTDO[]>(
                `${ApiURL.Tickets}/${id}/comments`,
                {content: message},
                {completion: this.globalLoader});
        }
    }

    public async saveCommentFile(ticketsId: string, commentId: string, file: File) {
        const formData = new FormData();
        formData.append('file', file);

        return ApiService.postData<CommentTDO[]>(
            `${ApiURL.Tickets}/${ticketsId}/comments/${commentId}/attachments`,
            formData,
            {
                contentType: AjaxContentType.FORM_DATA,
                completion: this.globalLoader
            });
    }

    public loadComponents = async (id?: string) => {
        const deviceId = id || this.filtersStore.selected.plant;
        if (deviceId && !this.components.get(deviceId)) {
            //TODO: Use API when it will be fixed
            // const {data} = await ApiService.getData<ComponentTDO[]>(`${ApiURL.Devices}/${deviceId}/components`, {}, {completion: this.drawerLoader});
            this.components.set(deviceId, DEVICE_COMPONENTS);
        }
    };

    public async deleteTask(taskId: string) {
        await ApiService.deleteData<MaintenanceDTO>(`${ApiURL.MaintenanceTasks}/${taskId}`, {completion: this.globalLoader});
    }

    public  saveTask = async (task: MaintenanceDTO) => {
        const {plant, customer} = this.filtersStore.selected;
        const params: NewMaintenanceDTO = {
            description: task.description,
            deviceId: plant,
            organizationId: customer,
            isRecurring: !!task.isRecurring,
        };
        if (task.type === TaskType.data) {
            params.measurement = task.measurement;
            params.valueDelta = task.valueDelta;
        }
        if (task.type === TaskType.scheduled) {
            params.timeDelta = `P${task?.timeD??0}DT${task?.timeH??0}H`;
            if(task.initialTime){
                params.initialTime = DateTimeService.toJSONLocal(task.initialTime);
            }
        }

        if (task.taskId) {
            params.status = task.status;
            params.logMessage = task?.userLog?.message;
            if (task.type === TaskType.data) {
                params.value = task.value;
            }
            await ApiService.patchData<MaintenanceDTO>(`${ApiURL.MaintenanceTasks}/${task.taskId}`, params, {completion: this.globalLoader});
        } else {
            await ApiService.postData<MaintenanceDTO>(`${ApiURL.MaintenanceTasks}`, params, {completion: this.globalLoader});
        }
    }

    public async saveTicket(ticket: SupportTicketDTO, deletedFiles: string[], files: File[], comments: CommentTDO[]) {
        ticket.componentIds = ticket.componentId ? [ticket.componentId] : [];
        ticket.componentId = undefined;

        let reqList: Array<Promise<void>> = [];
        let ticketId = ticket.id;

        const _uploader = (ticketId: string, file: File) => {
            return new Promise<void>(async resolve => {
                const resp = await this.saveMessage(ticketId, '');
                const commentId = ((resp as any).headers.location).split('/').reverse()[0];
                if (commentId) {
                    await this.saveCommentFile(ticketId, commentId, file);
                    resolve();
                }
            });
        };


        if (ticketId) {
            await ApiService.patchData<SupportTicketDTO>(`${ApiURL.Tickets}/${ticketId}`,
                {
                    userId: !ticket.userId ? undefined : ticket.userId, //ticket.userId === ticket.user?.displayName ? undefined : ticket.userId,
                    componentIds: ticket.componentIds,
                    status: ticket.status,
                    description: ticket.description,
                }, {completion: this.globalLoader});
        } else {
            const newTicketResp = await ApiService.postData<SupportTicketDTO>(`${ApiURL.Tickets}`, ticket, {completion: this.globalLoader});
            ticketId = ((newTicketResp as any).headers.location).split('/').reverse()[0];
        }

        void runInAction(async () => {
            deletedFiles.forEach((fileId) => {
                const comment = comments.find(c => c.attachments.length && c.attachments[0].id === fileId);
                if (comment) {
                    reqList.push(ApiService.deleteData(`${ApiURL.Tickets}/${ticketId}/comments/${comment.id}/attachments/${fileId}`).then(() => {
                    }));
                }
            });
            files.forEach((file) => {
                reqList.push(_uploader(ticketId, file));
            });
            const saveAll = Promise.all(reqList);
            this.globalLoader.subscribe(saveAll);
            await saveAll;
        });
    }

    @action
    public onServiceRangeChange = (data: ValueChangedInfo) => {
        const from = data.value[0] ? data.value[0] : '';
        const to = data.value[1] ? DateTimeService.endOfDay(data.value[1]) : '';
        this.selectedServiceRange = [from, to];
    };

    @action
    public onServiceRangeClosed = () => {
        const needRefresh = JSON.stringify(this.serviceRangeFilter) !== JSON.stringify(this.selectedServiceRange);
        this.serviceRangeFilter = this.selectedServiceRange;
        if (needRefresh) {
            void this.loadTickets();
        }
    };

    @action
    public onServiceUpdatedChange = (value: ServiceChangeKey) => {
        this.serviceUpdatedFilter = value;
        void this.loadTickets();
    };

    @action
    public showGridReset = () => {
        this.resetGridVisible = true;
    };

    @action
    public hideGridReset = () => {
        this.resetGridVisible = false;
    };

    public convertToTicket = (_: MaintenanceDTO) => {
        return;
    };
}

export const AppStoreContext = createContext({} as AppStore);
